import { isAccommodationV2AccommodationOfferView, isAccommodationV2Item, isBedbankItem, isExperienceItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { buildExperiencesPassportFromOffers } from 'api/mappers/experienceMap'
import type { ExperiencesPassport, ExperiencesPassportOfferSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'
import { isHotel, isLEHotel } from 'lib/offer/offerTypes'

export type ExperiencesPassportAccommodationItem =
  | App.Checkout.InstantBookingLEHotelItem
  | App.Checkout.BedbankHotelItem
  | App.Checkout.AccommodationV2Item

export function getExperiencesPassportAccommodationItems(items: Array<App.Checkout.AnyItem>): Array<ExperiencesPassportAccommodationItem> {
  return items.filter((item): item is ExperiencesPassportAccommodationItem =>
    isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isAccommodationV2Item(item))
}

export function getExperiencesPassportCheckoutKey(
  cart: App.CheckoutCartState,
  sessions: App.CheckoutState['accommodationSessions'] = {},
): string | undefined {
  const accommodations = getExperiencesPassportAccommodationItems(cart.items)
  if (!accommodations.length && !cart.items.some(isExperiencesPassportItem)) return undefined

  return JSON.stringify([
    cart.transactionKey,
    cart.regionCode,
    cart.currencyCode,
    ...accommodations
      .map(item => {
        const key = [item.itemId, item.offerId, item.checkIn, item.checkOut]
        if (isAccommodationV2Item(item)) {
          const rate = item.sessionId ? sessions[item.sessionId]?.rate : undefined
          key.push(
            item.sessionId ?? '',
            rate?.service.type === 'lpc' ? 'reservation' : rate?.service.type ?? item.internalService,
            rate?.service.type === 'lpc' ? rate.service.offerId : rate?.service.propertyId ?? item.offerId,
            rate?.service.rateId ?? item.internalRateId ?? '',
          )
        }
        return key
      })
      .sort((first, second) => first[0].localeCompare(second[0])),
  ])
}

export function isExperiencesPassportItem(item: App.Checkout.AnyItem): item is App.Checkout.ExperienceItem {
  return isExperienceItem(item) && !!item.experiencesPassport
}

function getExperiencesPassportItemKey(item: App.Checkout.ExperienceItem): string {
  // The parent item ID identifies the booked room; roomId only identifies its room type.
  return JSON.stringify([item.accommodationParentItemId, item.experienceId])
}

export function reconcileExperiencesPassportCheckoutItems(
  currentItems: Array<App.Checkout.AnyItem>,
  desiredItems: Array<App.Checkout.ExperienceItem>,
): Array<App.Checkout.AnyItem> {
  const desiredByKey = new Map(desiredItems.map(item => [getExperiencesPassportItemKey(item), item]))
  const keptKeys = new Set<string>()
  const nextItems: Array<App.Checkout.AnyItem> = []
  let changed = false

  for (const item of currentItems) {
    if (!isExperiencesPassportItem(item)) {
      nextItems.push(item)
      continue
    }

    const key = getExperiencesPassportItemKey(item)
    const desired = desiredByKey.get(key)
    if (!desired || keptKeys.has(key)) {
      changed = true
      continue
    }

    keptKeys.add(key)
    const updated = { ...desired, itemId: item.itemId, transactionKey: item.transactionKey }
    if (JSON.stringify(item) === JSON.stringify(updated)) {
      nextItems.push(item)
    } else {
      nextItems.push(updated)
      changed = true
    }
  }

  for (const [key, desired] of desiredByKey) {
    if (keptKeys.has(key)) continue
    nextItems.push(desired)
    changed = true
  }

  return changed ? nextItems : currentItems
}

export function getExperiencesPassportCheckoutChanges(
  currentItems: Array<App.Checkout.AnyItem>,
  desiredItems: Array<App.Checkout.ExperienceItem>,
): {
  toAdd: Array<App.Checkout.ExperienceItem>
  toUpdate: Array<App.Checkout.ExperienceItem>
  toRemove: Array<string>
} {
  const nextItems = reconcileExperiencesPassportCheckoutItems(currentItems, desiredItems)
  const currentById = new Map(currentItems.filter(isExperiencesPassportItem).map(item => [item.itemId, item]))
  const nextById = new Map(nextItems.filter(isExperiencesPassportItem).map(item => [item.itemId, item]))

  return {
    toAdd: [...nextById.values()].filter(item => !currentById.has(item.itemId)),
    toUpdate: [...nextById.values()].filter(item => currentById.has(item.itemId) && currentById.get(item.itemId) !== item),
    toRemove: [...currentById.keys()].filter(itemId => !nextById.has(itemId)),
  }
}

export function getExperiencesPassportForAccommodationItem(
  offerView: App.Checkout.AccommodationOfferView,
  accommodationItemId: string,
  experienceViews: App.WithDataStatus<Array<App.Checkout.ExperienceItemView>>,
  currencyCode: string,
): ExperiencesPassport | undefined {
  if (!experienceViews.hasRequiredData) return undefined

  let source: ExperiencesPassportOfferSource
  if (isAccommodationV2AccommodationOfferView(offerView)) {
    const property = offerView.property
    if (!property) return undefined

    source = {
      id: property.id,
      type: offerView.offerType,
      location: offerView.location,
      property: {
        name: property.name,
        latitude: property.location.latitude,
        longitude: property.location.longitude,
      },
    }
  } else {
    if (!('offer' in offerView) || !isHotel(offerView.offer)) return undefined
    source = offerView.offer
  }

  const passportExperiences = experienceViews.data
    .filter(view => view.item.experiencesPassport && view.item.accommodationParentItemId === accommodationItemId)
    .map(view => view.experience)

  return buildExperiencesPassportFromOffers(source, passportExperiences, currencyCode)
}

export function getAccommodationV2PassportPackageId(
  item: App.Checkout.AccommodationV2Item,
  sessions: App.CheckoutState['accommodationSessions'],
  offers: Record<string, App.AnyOffer | undefined>,
): string | undefined {
  const session = item.sessionId ? sessions[item.sessionId]?.data : undefined
  if (!session || session.id !== item.sessionId) return undefined
  if (session.data.supplierMetaData.internalService === 'bedbank') {
    return session.data.supplierMetaData.internalRoomId
  }
  const rate = item.sessionId ? sessions[item.sessionId]?.rate : undefined
  const offer = rate?.service.type === 'lpc' ? offers[rate.service.offerId] : undefined
  if (rate?.service.type !== 'lpc' || !isLEHotel(offer) || !('packages' in offer)) return undefined
  const packageId = rate.service.packageId
  return offer.packages.find(pkg => pkg.id === packageId)?.lePackageId
}
